import { useFormSubmit } from "@/hooks/useFormSubmit";

interface LocavoreFormProps {
  title?: string;
  description?: string;
  submitLabel?: string;
}

export default function LocavoreForm({
  title = "Submit a Locavore",
  description = "Know a restaurant, farm, market, maker or destination Eddie should discover? Point him there.",
  submitLabel = "Submit a Locavore",
}: LocavoreFormProps) {
  const endpoint = "https://readdy.ai/api/form/db23tapggrtvtk1bh0ig";
  const { status, message, handleSubmit } = useFormSubmit(endpoint);

  return (
    <div className="w-full">
      <h2 className="font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
        {title}
      </h2>
      <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-foreground-700">
        {description}
      </p>

      <form
        id="submit-a-locavore"
        data-readdy-form
        onSubmit={handleSubmit}
        noValidate
        className="mt-10"
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-7 md:grid-cols-2">
          <div>
            <label htmlFor="lv-name" className="field-label">
              Name
            </label>
            <input
              id="lv-name"
              name="name"
              type="text"
              required
              autoComplete="name"
              placeholder="Your name"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="lv-email" className="field-label">
              Email
            </label>
            <input
              id="lv-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="lv-business" className="field-label">
              Business / Person
            </label>
            <input
              id="lv-business"
              name="business"
              type="text"
              placeholder="Who should Eddie meet?"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="lv-city" className="field-label">
              City
            </label>
            <input
              id="lv-city"
              name="city"
              type="text"
              placeholder="City, region, country"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="lv-website" className="field-label">
              Website
            </label>
            <input
              id="lv-website"
              name="website"
              type="text"
              placeholder="https://"
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="lv-social" className="field-label">
              Social Media
            </label>
            <input
              id="lv-social"
              name="social"
              type="text"
              placeholder="@handle or link"
              className="field-input"
            />
          </div>
        </div>

        <div className="mt-9">
          <label htmlFor="lv-why" className="field-label">
            Why should Eddie visit?
          </label>
          <textarea
            id="lv-why"
            name="why"
            rows={4}
            maxLength={500}
            placeholder="What makes this place, person or business worth discovering? (500 characters max)"
            className="field-textarea"
          />
        </div>

        <div className="mt-9">
          <label htmlFor="lv-additional" className="field-label">
            Additional Information
          </label>
          <textarea
            id="lv-additional"
            name="additional"
            rows={3}
            maxLength={500}
            placeholder="Anything else Eddie should know (500 characters max)."
            className="field-textarea"
          />
        </div>

        <div className="mt-9">
          <label htmlFor="lv-photos" className="field-label">
            Upload Photos
          </label>
          <input
            id="lv-photos"
            name="photos"
            type="file"
            accept="image/*"
            multiple
            className="block w-full cursor-pointer font-body text-sm text-foreground-600 file:mr-4 file:cursor-pointer file:rounded-md file:border file:border-foreground-950/25 file:bg-transparent file:px-5 file:py-2.5 file:font-label file:text-[10px] file:uppercase file:tracking-label file:text-foreground-950 hover:file:border-foreground-950/50"
          />
          <p className="mt-2 font-body text-xs text-foreground-500">
            Photos help Eddie plan — links work too if uploading isn't easy.
          </p>
        </div>

        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-aux"
        />

        <div className="mt-10 flex flex-col gap-5 sm:flex-row sm:items-center">
          <button
            type="submit"
            disabled={status === "submitting"}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "submitting" ? "Sending..." : submitLabel}
            {status !== "submitting" && (
              <i className="ri-send-plane-line text-base" />
            )}
          </button>

          {status === "success" && (
            <p className="flex items-center gap-2 font-body text-sm text-primary-700">
              <i className="ri-check-line text-base" />
              {message}
            </p>
          )}
          {status === "error" && (
            <p className="flex items-center gap-2 font-body text-sm text-red-700">
              <i className="ri-error-warning-line text-base" />
              {message}
            </p>
          )}
        </div>
      </form>
    </div>
  );
}